import { QueryClient } from "@tanstack/react-query";

// Create a query client with sensible defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Consider data fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Keep inactive queries for 10 minutes
      gcTime: 10 * 60_000,
      // Retry failed requests up to 3 times with exponential backoff
      retry: (failureCount, error: any) => {
        // Don't retry on authentication errors (401, 403)
        if (error?.status === 401 || error?.status === 403) {
          return false;
        }
        // Don't retry on client errors (4xx)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      // Enable background refetching when window regains focus
      refetchOnWindowFocus: true,
      // Don't refetch on mount if data is fresh
      refetchOnMount: false,
    },
    mutations: {
      // Retry mutations once
      retry: 1,
    },
  },
});

// Query keys factory for consistency
export const queryKeys = {
  // Auth
  auth: ["auth"] as const,
  profile: () => [...queryKeys.auth, "profile"] as const,

  // Coach
  coach: ["coach"] as const,
  chatHistory: (userId: string) =>
    [...queryKeys.coach, "chat-history", userId] as const,

  // Guides (local storage)
  guides: ["guides"] as const,
  allGuides: () => [...queryKeys.guides, "all"] as const,
  guide: (id: string) => [...queryKeys.guides, id] as const,
  guidesByCategory: (categoryId: string | null) =>
    [...queryKeys.guides, "by-category", categoryId] as const,
  guidesSearch: (query: string) =>
    [...queryKeys.guides, "search", query] as const,
  guidesByTags: (tags: string[]) =>
    [...queryKeys.guides, "by-tags", tags] as const,

  // Categories
  categories: ["categories"] as const,
  allCategories: () => [...queryKeys.categories, "all"] as const,
  category: (id: string) => [...queryKeys.categories, id] as const,

  // Goals (local storage)
  goals: ["goals"] as const,
  allGoals: () => [...queryKeys.goals, "all"] as const,
  goal: (id: string) => [...queryKeys.goals, id] as const,

  // Tasks (local storage)
  tasks: ["tasks"] as const,
  allTasks: () => [...queryKeys.tasks, "all"] as const,
  task: (id: string) => [...queryKeys.tasks, id] as const,
  tasksByStatus: (status: string, date?: string) =>
    [...queryKeys.tasks, "by-status", status, date] as const,
} as const;
